import type { AnySchema, Schema } from 'yup'
import { date, number, object, string } from 'yup'
import type { Calendar } from '@/prisma/client'

type TranslateFunction = (key: string, params?: Record<string, any>) => string

export function calendarSchema(fields: string[], t: TranslateFunction) {
  const baseSchema = {
    start: date().required(t('errors.required')),
    end: date().required(t('errors.required'))
      .min(
        new Date(new Date().setHours(0, 0, 0, 0)),
        t('errors.dateMin'),
      )
      .test('end-after-start', t('errors.endAfterStart'), function (value) {
        return !value || value > this.parent.start
      }),
    resourceId: number().required(t('errors.required')),
  }

  type BaseSchemaKeys = keyof typeof baseSchema
  const partialSchema: Partial<Record<BaseSchemaKeys, AnySchema>> = {}
  fields.forEach((fieldName) => {
    if (fieldName in baseSchema) {
      const key = fieldName as BaseSchemaKeys
      partialSchema[key] = baseSchema[key]
    }
  })
  return object(partialSchema)
}

export const useCalendarStore = defineStore('calendar', () => {
  const calendars = ref<Calendar[]>([])
  const serviceCalendar = ref<{
    service: any
    startDate: string
    endDate: string
    weekOfYear: number
    resources: any[]
    closingDays: any[]
  } | null>(null)

  async function fetchServiceCalendar(serviceId: number, weekOfYear: number) {
    try {
      serviceCalendar.value = await $fetch(`/api/service/${serviceId}/calendar`, {
        query: { weekOfYear },
      })
      return { data: serviceCalendar.value, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  async function createCalendar(calendar: any) {
    try {
      const result = await $fetch<Calendar | null>('/api/calendar', {
        method: 'POST',
        body: calendar,
      })

      if (result) {
        calendars.value = [...(calendars.value || []), result]
      }
      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  async function updateCalendar(id: number, calendar: any) {
    try {
      const result = await $fetch<Calendar | null>(`/api/calendar/${id}`, {
        method: 'PUT',
        body: calendar,
      })

      if (result) {
        const index = calendars.value?.findIndex(c => c.id === id)
        if (index !== undefined && index > -1) {
          calendars.value?.splice(index, 1, result)
        }
      }
      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  async function deleteCalendar(id: number) {
    try {
      const result = await $fetch<Calendar | null>(`/api/calendar/${id}`, {
        method: 'DELETE',
      })

      if (result) {
        const index = calendars.value?.findIndex(c => c.id === id)
        if (index !== -1) {
          calendars.value?.splice(index, 1)
        }
      }
      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  // Helper to get current week of year
  function getCurrentWeekOfYear() {
    const now = new Date()
    const start = new Date(now.getFullYear(), 0, 1)
    const diff = (now.getTime() - start.getTime()) + ((start.getTimezoneOffset() - now.getTimezoneOffset()) * 60 * 1000)
    const oneDay = 1000 * 60 * 60 * 24
    const day = Math.floor(diff / oneDay)
    return Math.ceil(day / 7)
  }

  return {
    fetchServiceCalendar,
    createCalendar,
    updateCalendar,
    deleteCalendar,
    getCurrentWeekOfYear,
    calendars,
    serviceCalendar,
  }
})
